import React, { useCallback, useRef, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Button,
  CardContent,
  Stack,
  Tooltip,
  IconButton,
  Card,
  CircularProgress,
  useTheme,
} from "@mui/material";
import CircleIcon from "@mui/icons-material/Circle";
import SegmentComponent from "./WorkOrderSegment";
import WorkOrderSegmentUpdate from "./WorkOrderSegmentUpdate";
import { FieldArray } from "formik";
import { useSelector } from "react-redux";
import { RootState } from "../../../app";
import { useAppDispatch } from "../../../app/hooks";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import { setSegmentIndex } from "../../store/queueBoardSlice";
import { Segment } from "../../QueueBoard/models/workOrderTypes";
import WorkOrderSegmentDetails from "./details/WorkOrderSegmentDetailsDialog";
import WorkOrderQueueAPI from "../../QueueBoard/api/workOrderApi";
import { ButtonStyle } from "../../QueueBoard/models/workOrderStyles";
import packageJson from "../../../../package.json";
import { logAmplitudeEventWithProps } from "components/common/amplitude/amplitude";
import { User } from "../../authentication/models/authTypes";
import localStorageHelper from "../../../app/localStorage";
import InfoIcon from "@mui/icons-material/Info";
import { useSnackbar } from "notistack";
import { ensureEditableWorkOrder } from "../utils/finalizationHelpers";
interface WorkOrderSegmentListProps {
  segments: Segment[] | any[];
  workOrderNumber?: string;
  isFinalized?: boolean;
  invoiceDocumentIdentity?: number;
}

const WorkOrderSegmentList = ({
  segments,
  workOrderNumber,
  isFinalized = false,
  invoiceDocumentIdentity,
}: WorkOrderSegmentListProps) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const [openSegmentDialog, setOpenSegmentDialog] = useState(false);
  const [newSegmentDialog, setNewSegDialog] = useState(false);
  const { segmentIndex } = useSelector(
    (state: RootState) => state.workOrderDashboard
  );
  const segmentsFieldRef = useRef(null);
  const [user] = useState<User | undefined>(
    localStorageHelper.getByKey("user")
  );
  const { enqueueSnackbar } = useSnackbar();
  const verifyEditable = useCallback(async () => {
    return ensureEditableWorkOrder(invoiceDocumentIdentity, enqueueSnackbar);
  }, [invoiceDocumentIdentity, enqueueSnackbar]);
  //Segment Dialog Close
  const handleSegmentClose = () => {
    setNewSegDialog(false);
    logAmplitudeEventWithProps(
      user.userNameAndCompany,
      {
        company: user.company,
        user: user.userName,
      },
      "closeNewSegmentDialog",
      {
        elapsed_ms: "124",
        title: "Segment List",
        componentName: "WorkOrderSegment-list.tsx",
        version: packageJson.version,
      }
    );
  };
  //Segment Dialog Open
  const segmentDialogHandler = async () => {
    if (isFinalized) return;
    if (!(await verifyEditable())) {
      return;
    }
    setNewSegDialog(true);

    logAmplitudeEventWithProps(
      user.userNameAndCompany,
      {
        company: user.company,
        user: user.userName,
      },
      "openNewSegmentDialog",
      {
        elapsed_ms: "124",
        title: "Segment List",
        componentName: "WorkOrderSegment-list.tsx",
        version: packageJson.version,
      }
    );
  };
  //  let _segmentData: Segment = null;
  const [segmentDetails, setSegmentDetails] = useState<Segment | null>(null);
  const handleExSegmentOn = (segment) => {
    setSegmentDetails(segment);
    //_segmentData = segment;
    //dispatch(setCurrentSegment(_segmentData));
    setOpenSegmentDialog(true);
  };
  const [showProgressBar, setShowProgressBar] = useState(false);
  const handleExSegmentOf = () => {
    setOpenSegmentDialog(false);
  };

  const updateSegment = (updatedSegment) => {
    if (segmentsFieldRef.current) {
      segmentsFieldRef.current(`segments[${segmentIndex}]`, {
        ...segments[segmentIndex], // Keep existing properties
        ...updatedSegment, // Overwrite with new data
      });
      setOpenSegmentDialog(false);
    }

    logAmplitudeEventWithProps(
      user.userNameAndCompany,
      {
        company: user.company,
        user: user.userName,
      },
      "closeUpdateSegmentDialog",
      {
        elapsed_ms: "124",
        title: "Segment List",
        componentName: "WorkOrderSegment-list.tsx",
        version: packageJson.version,
      }
    );
  };

  const [showImageCommentDialog, setShowImageCommentDialog] = useState(false);
  const closeSegmentDetailHandler = (IsSegmentDetailToClose: boolean) => {
    setShowImageCommentDialog(IsSegmentDetailToClose);
  };
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedSegment, setSelectedSegment] = useState(null);
  const getSegmentDetails = async (segment: Segment) => {
    if (segment?.identity) {
      setShowProgressBar(true);
      const data = await WorkOrderQueueAPI.getSegmentDetails(segment.identity);
      if (data) {
        setSelectedSegment(segment);
        setSelectedItem(data);
        data.memo = segment.memo;
        data.standardJobSteps = segment.standardJobSteps;
        data.workOrderNumber = workOrderNumber; // Add work order number to the data
        setShowImageCommentDialog(true);
      }
      setShowProgressBar(false);

      logAmplitudeEventWithProps(
        user.userNameAndCompany,
        {
          company: user.company,
          user: user.userName,
        },
        "fetchSegmentDetails",
        {
          elapsed_ms: "124",
          title: "Segment List",
          componentName: "WorkOrderSegment-list.tsx",
          version: packageJson.version,
        }
      );
    }
  };
  return (
    <Box mt={2} sx={{ minHeight: "535px" }}>
      <FieldArray name="segments">
        {({ form: { setFieldValue }, remove, push }) => {
          segmentsFieldRef.current = setFieldValue; // Store the setFieldValue reference

          return (
            <React.Fragment>
              {segments.length > 0
                ? segments.map((segment, index) => (
                    <Card
                      key={index}
                      sx={{
                        mb: 2,
                        border: `1px solid ${theme.palette.divider}`,
                        "&:hover": {
                          backgroundColor:
                            theme.palette.mode === "light"
                              ? "#f5f5f5"
                              : "rgba(255, 255, 255, 0.08)",
                          // cursor: "pointer",
                        },
                      }}
                      // onClick={() => {
                      //   dispatch(setSegmentIndex(index));
                      //   handleExSegmentOn(segment);
                      // }}
                    >
                      <CardContent
                        sx={{
                          marginTop: "0px",
                          paddingTop: "0px",
                        }}
                      >
                        <Grid
                          container
                          spacing={1}
                          sx={{ mt: 1 }}
                          alignItems="center"
                        >
                          {segment.isTimeOn === true ? (
                            <Grid item xs={0.2}>
                              <CircleIcon
                                sx={{
                                  fontSize: "10px",
                                  boxShadow: "revert",
                                  color:
                                    segment.isTimeOn === true ? "#3DA554" : "",
                                }}
                              />
                            </Grid>
                          ) : (
                            ""
                          )}

                          <Grid
                            item
                            xs={6}
                            sx={{
                              ml: segment.isTimeOn === true ? "5px" : "0px",
                              //ml: "5px",
                            }}
                          >
                            <Typography
                              sx={{
                                fontStyle: "normal",
                                fontWeight: 400,
                                fontSize: "18px",
                                color: theme.palette.text.primary,
                              }}
                            >
                              {segment.displayText}
                            </Typography>
                          </Grid>

                          <Grid
                            item
                            container
                            xs
                            justifyContent="flex-end"
                            alignItems="center"
                            spacing={1}
                            sx={{ display: "flex" }} // Ensure it stays as a single row
                          >
                            <Grid item>
                              {segment.clockedHours ? (
                                <Typography
                                  sx={{
                                    lineHeight: "2.1",
                                    borderRadius: "3px",
                                    fontSize: "10px",
                                    fontWeight: 400,
                                    color: theme.palette.text.primary,
                                    padding: "3px",
                                    mr: "5px",
                                    fontStyle: "normal",
                                    backgroundColor:
                                      theme.palette.mode === "light"
                                        ? "#EAECEF"
                                        : "rgba(255, 255, 255, 0.12)",
                                    whiteSpace: "nowrap", // Prevents text from wrapping
                                    overflow: "hidden", // Optional: hides overflow text if container is too small
                                    textOverflow: "ellipsis", // Optional: adds "..." if text overflows
                                  }}
                                  variant="caption"
                                  color="textMain"
                                >
                                  {`${segment.clockedHours.toFixed(
                                    2
                                  )} hours Clocked`}
                                </Typography>
                              ) : null}
                            </Grid>

                            <Grid item>
                              {segment.hasAttachment ? (
                                <Typography
                                  sx={{
                                    lineHeight: "2.1",
                                    borderRadius: "3px",
                                    ml: "5px",
                                    fontSize: "10px",
                                    fontStyle: "normal",
                                    fontWeight: 400,
                                    padding: "3px",
                                    backgroundColor:
                                      theme.palette.mode === "light"
                                        ? "#E5F2E3"
                                        : "rgba(76, 175, 80, 0.2)",
                                    color:
                                      theme.palette.mode === "light"
                                        ? "#21592D"
                                        : "#4caf50",
                                  }}
                                  variant="caption"
                                >
                                  Images Added
                                </Typography>
                              ) : null}
                            </Grid>
                          </Grid>
                          <Grid item xs={12}>
                            <Typography
                              sx={{
                                fontWeight: 400,
                                fontSize: "12px",
                                fontStyle: "normal",
                              }}
                            >
                              {segment.memo}
                            </Typography>
                          </Grid>
                        </Grid>
                        <Stack
                          direction="row"
                          spacing={2}
                          justifyContent="end"
                          alignItems="center"
                        >
                          <Tooltip
                            title={
                              isFinalized
                                ? "Work order is finalized"
                                : "Edit Segment"
                            }
                          >
                            <span>
                              <IconButton
                                sx={{
                                  width: 32,
                                  height: 32,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                                disabled={isFinalized}
                                onClick={async () => {
                                  if (isFinalized) return;
                                  if (!(await verifyEditable())) {
                                    return;
                                  }
                                  dispatch(setSegmentIndex(index));
                                  handleExSegmentOn(segment);
                                }}
                              >
                                <ModeEditIcon
                                  sx={{
                                    width: "18px",
                                    height: "18px",
                                    "&:hover": {
                                      cursor: "pointer",
                                      color: "#2371E7",
                                    },
                                    color: "gray",
                                  }}
                                />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="View Segment Details">
                            <IconButton
                              sx={{
                                width: 32,
                                height: 32,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                              onClick={() => {
                                getSegmentDetails(segment);
                              }}
                            >
                              <InfoIcon
                                sx={{
                                  width: "18px",
                                  height: "18px",
                                  "&:hover": {
                                    cursor: "pointer",
                                    color: "#2371E7",
                                  },
                                  color: "gray",
                                }}
                              />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </CardContent>
                    </Card>
                  ))
                : null}

              {openSegmentDialog && !isFinalized && (
                <WorkOrderSegmentUpdate
                  open={openSegmentDialog}
                  currentSegment={segmentDetails}
                  handleCloseEx={handleExSegmentOf}
                  update={updateSegment}
                  invoiceDocumentIdentity={invoiceDocumentIdentity}
                />
              )}

              {showImageCommentDialog && (
                <WorkOrderSegmentDetails
                  currentSegmentDetails={selectedItem}
                  dialogToOpen={showImageCommentDialog}
                  closeSegmentDetailDialog={closeSegmentDetailHandler}
                  isReadOnly={isFinalized}
                  invoiceDocumentIdentity={invoiceDocumentIdentity}
                />
              )}

              <Grid>
                <Grid item xs={12} sx={{ textAlign: "right" }}>
                  <Button
                    fullWidth
                    onClick={segmentDialogHandler}
                    // onClick={() => {
                    //   // push({ displayText: "Adeeel: " });
                    //   setNewSegDialog(true);
                    //   console.log(segments);
                    // }}
                    size="small"
                    sx={{ ...ButtonStyle }}
                    variant="contained"
                    disabled={isFinalized}
                  >
                    New Work Segment
                  </Button>
                  {newSegmentDialog && !isFinalized && (
                    <SegmentComponent
                      push={push}
                      open={true}
                      handleClose={handleSegmentClose}
                      invoiceDocumentIdentity={invoiceDocumentIdentity}
                    />
                  )}
                </Grid>
              </Grid>
            </React.Fragment>
          );
        }}
      </FieldArray>
      {showProgressBar && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "80vh", // Make sure the height takes the full viewport height or the height of the dialog
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            zIndex: 1300, // Ensure it's above other elements in the dialog
            backgroundColor: "rgba(255, 255, 255, 0.7)", // Add some transparency to indicate loading
          }}
        >
          <CircularProgress sx={{ color: "#2371E7" }} size={48} />
        </Box>
      )}
    </Box>
  );
};

export default WorkOrderSegmentList;
